import { useState, useEffect, useCallback } from 'react';

// Stale-tab protection: detects that a newer portal build has been deployed
// while this tab keeps running the old bundle (old tabs keep their old read
// patterns — and eventually 404 on lazy chunks the deploy removed).
//
// Hosting only: compares the hash of the running main bundle with
// /asset-manifest.json. No Firebase / RTDB access at all. Never reloads by
// itself — an agent may be mid-booking; the caller shows a notice.

const POLL_INTERVAL_MS = 10 * 60 * 1000;   // background poll
const WAKE_MIN_GAP_MS = 2 * 60 * 1000;     // focus / visibility: at most one check per 2 min
const REMIND_AFTER_DISMISS_MS = 30 * 60 * 1000;

// "/static/js/main.040f4aae.js" (or an absolute URL) -> "main.040f4aae.js"
const mainBundleName = (src) => {
  const m = /\/static\/js\/(main\.[^/?#]+\.js)/.exec(String(src || ''));
  return m ? m[1] : null;
};

const runningBundleName = () => {
  if (typeof document === 'undefined') return null;
  const el = document.querySelector('script[src*="/static/js/main."]');
  return el ? mainBundleName(el.getAttribute('src')) : null;
};

export default function useAppVersionWatcher() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const running = runningBundleName();
    // Dev server (bundle.js) or an unrecognised shell: nothing to compare.
    if (!running) return undefined;

    let stopped = false;
    let checking = false;
    let lastCheckAt = 0;
    let timer = null;

    const onWake = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastCheckAt < WAKE_MIN_GAP_MS) return;
      check();
    };

    const stop = () => {
      stopped = true;
      if (timer) clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };

    async function check() {
      if (stopped || checking) return;
      checking = true;
      lastCheckAt = Date.now();
      try {
        const res = await fetch('/asset-manifest.json', { cache: 'no-store' });
        if (!res.ok) return;
        const manifest = await res.json();
        const latest = mainBundleName(manifest && manifest.files && manifest.files['main.js']);
        if (!stopped && latest && latest !== running) {
          setUpdateAvailable(true);
          stop(); // detected once — no more polling
        }
      } catch (_) {
        // Offline, mid-deploy or a non-JSON answer: try again on the next tick.
      } finally {
        checking = false;
      }
    }

    timer = setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return stop;
  }, []);

  // Dismissed: hide now, remind again 30 minutes later (rather than never).
  useEffect(() => {
    if (!dismissed) return undefined;
    const reminder = setTimeout(() => setDismissed(false), REMIND_AFTER_DISMISS_MS);
    return () => clearTimeout(reminder);
  }, [dismissed]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return { updateAvailable, visible: updateAvailable && !dismissed, dismiss };
}
